import React, { useEffect, useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  Card,
  Grid,
  Button,
  Box,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Slide,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Divider,
  Link,
  AppBar,
  Toolbar,
  Avatar,
  Menu,
  MenuItem,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import { googleLogout } from "@react-oauth/google";

import SoftBox from "components/SoftBox";
import SoftSelect from "components/SoftSelect";
import logo from "assets/images/Anser.png";
import cartoonImage from "assets/images/Login-amico.svg";
import { useAuth } from "context/AuthContext";
import axiosInstance from "utils/axiosConfig";
import { FAST_API_BASE_URL, LAMBDA_BASE_URL } from "constants";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useSystemTheme } from "context/SystemThemeContext";

// Session configuration constants
const SESSION_CONFIG = {
  SHORT_SESSION: 15 * 60 * 1000, // 15 minutes
  EXTENDED_SESSION: 30 * 24 * 60 * 60 * 1000, // 30 days
};

const Auth = () => {
  const { mode } = useSystemTheme();
  const isDarkMode = mode === "dark";
  
  const navigate = useNavigate();
  const { login, logout, user, isAuthenticated,getDomains } = useAuth();
  const { referralCode } = useParams();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleSignInLoading, setGoogleSignInLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  
  const [showOtpField, setShowOtpField] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotMessage, setForgotMessage] = useState("");

  const [showSignupForm, setShowSignupForm] = useState(false);
  const [signupFormData, setSignupFormData] = useState({
    firstName: "",
    lastName: "",
    password: "",
    confirmPassword: "",
  });
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [registrationFormData, setRegistrationFormData] = useState({
    domainName: "",
    companyName: "",
    websitePlatform: "",
  });
  const [domainRegistrationLoading, setDomainRegistrationLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [role, setRole] = useState("");
  const [Id, setId] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);

  const websitePlatforms = [
    { value: "wordpress", label: "WordPress" },
    { value: "webflow", label: "Webflow" },
    { value: "wix", label: "Wix" },
    { value: "squarespace", label: "Squarespace" },
    { value: "custom", label: "Others" },
  ];
  const initializeSession = (shouldRemember) => {
  const sessionConfig = {
    type: shouldRemember ? 'extended' : 'short',
    startTime: Date.now(),
    expiryTime: Date.now() + (shouldRemember ? SESSION_CONFIG.EXTENDED_SESSION : SESSION_CONFIG.SHORT_SESSION),
    lastActivity: Date.now(),
  };
  
  if (shouldRemember) {
    localStorage.setItem('sessionConfig', JSON.stringify(sessionConfig));
    localStorage.setItem('rememberMe', 'true');
  } else {
    sessionStorage.setItem('sessionConfig', JSON.stringify(sessionConfig));
    localStorage.setItem('rememberMe', 'false');
    localStorage.setItem('showStayLoggedInPrompt', 'true');
  }
};

  const passwordRules = {
    letter: /[A-Za-z]/.test(signupFormData.password),
    uppercase: /[A-Z]/.test(signupFormData.password),
    lowercase: /[a-z]/.test(signupFormData.password),
    number: /[0-9]/.test(signupFormData.password),
    specialChar: /[^A-Za-z0-9]/.test(signupFormData.password),
    minLength: signupFormData.password.length >= 8,
  };

  const allRulesPassed = Object.values(passwordRules).every(Boolean);

  const isLoggedIn = isAuthenticated();
  const userEmail = user?.email || "";
  const userName = user?.name || "";

  useEffect(() => {
    const checkAuthenticationAndRedirect = () => {
      if (!isLoggedIn) return;
      if (!user) return;
    
      const secret = queryParams.get("secret");
      const domainId = queryParams.get("domain_id");
      const roleParam = queryParams.get("role");
      const idParam = queryParams.get("id");
      const referralCodeFromQuery = queryParams.get("referral_code");
      const referralCodeFromPath = referralCode;
    
      const hasAuthParams = secret || domainId || roleParam || idParam || referralCodeFromQuery || referralCodeFromPath;
    
      if (hasAuthParams) return;
    
      if (!showRegistrationForm && !showSignupForm && !showOtpField && !showPasswordField && !forgotMode) {
        const isSuperAdmin = user?.is_super_admin === 1 || 
                           user?.is_super_admin === true || 
                           user?.is_super_admin === "true" ||
                           user?.is_super_admin === "1";
        const domains = getDomains();
        const hasDomains = domains && domains.length > 0;
      
        if (isSuperAdmin) {
          navigate("/customers");
        } else if (hasDomains && domains[0].role === "read only") {
          navigate("/dashboard");
        } else if (hasDomains) {
          navigate("/settings");
        }
      }
    };
  
    checkAuthenticationAndRedirect();
  }, [isLoggedIn, user, location.search, referralCode, navigate, showRegistrationForm,
    showSignupForm, showOtpField, showPasswordField, forgotMode
  ]);

  useEffect(() => {
    const roleParam = queryParams.get("role");
    const secret = queryParams.get("secret");
    const Id = queryParams.get("id");

    if (Id) {
      setId(Id);
    }

    if (roleParam) {
      setRole(roleParam);
    }

    const referralCodeFromPath = referralCode;
    const referralCodeFromQuery = queryParams.get("referral_code");
    
    const finalReferralCode = referralCodeFromPath || referralCodeFromQuery;
    
    if (finalReferralCode) {
      localStorage.setItem("referral_code", finalReferralCode);
    } else {
      localStorage.removeItem("referral_code");
    }

    const source = queryParams.get("source");
    if (source === "hubspot_marketplace") {
      localStorage.setItem("install_source", JSON.stringify({
        value: "hubspot_marketplace",
        timestamp: Date.now()
      }));
    }

    if (secret) {
      localStorage.setItem("secret", secret);
    } else {
      const domainId = queryParams.get("domain_id");
      if (domainId) {
        localStorage.setItem("domain_id", domainId);
      } else {
        localStorage.removeItem("secret");
        localStorage.removeItem("domain_id");
      }
    }
  }, [location.search, referralCode]);

  const getInstallSource = () => {
  try {
    const raw = localStorage.getItem("install_source");
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const THIRTY_MINUTES = 30 * 60 * 1000;

    if (Date.now() - parsed.timestamp > THIRTY_MINUTES) {
      localStorage.removeItem("install_source");
      return null;
    }

    return parsed.value;
  } catch {
    localStorage.removeItem("install_source");
    return null;
  }
};

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const resetAuthFlow = () => {
    setEmail("");
    setShowOtpField(false);
    setShowPasswordField(false);
    setShowSignupForm(false);
    setShowRegistrationForm(false);
    setForgotMode(false);
    setForgotEmail("");
    setForgotSuccess(false);
    setForgotMessage("");
    setOtp("");
    setPassword("");
    setRememberMe(false);
    setSignupFormData({
      firstName: "",
      lastName: "",
      password: "",
      confirmPassword: "",
    });
    setRegistrationFormData({
      domainName: "",
      companyName: "",
      websitePlatform: "",
    });
  };

  const enterForgotMode = () => {
    setForgotMode(true);
    setForgotEmail(email || "");
    setForgotMessage("");
    setForgotSuccess(false);
  };

  const exitForgotMode = () => {
    setForgotMode(false);
    setForgotEmail("");
    setForgotMessage("");
    setForgotSuccess(false);
  };

  const handleForgotEmailChange = (e) => {
    setForgotEmail(e.target.value);
    setForgotMessage("");
    setForgotSuccess(false);
  };

  const handleSendReset = async (e) => {
    e.preventDefault();
    const headers = getHeaders();

    if (!forgotEmail) {
      setForgotMessage("Please enter your email.");
      return;
    }

    setForgotLoading(true);
    try {
      const payload = { email: forgotEmail };
      const response = await axiosInstance.post(`${FAST_API_BASE_URL}/api/reset-password`, payload, {
        headers,
      });

      if (response && response.status === 200) {
        setForgotSuccess(true);
        setForgotMessage("We have sent you a link to your email.");
        showSnackbar("Reset link sent to your email!", "success");
      } else {
        setForgotSuccess(false);
        setForgotMessage("Failed to send reset link/ Email doesn't exist. Please try again.");
      }
    } catch (err) {
      console.error("Send reset failed:", err);
      setForgotSuccess(false);
      setForgotMessage("Failed to send reset link/ Email doesn't exist. Please try again.");
      showSnackbar("Failed to send reset link. Please try again.", "error");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      showSnackbar("Please enter a valid email address", "error");
      return;
    }

    setLoading(true);

    try {
      const response = await axiosInstance.post(`${FAST_API_BASE_URL}/api/signup`, {
        email: email,
      }, {
        headers: {
          "X-API-KEY": process.env.REACT_APP_API_KEY,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 200) {
        setShowOtpField(true);
        showSnackbar("Verification code sent to your email!", "success");
      }
    } catch (error) {
      console.error("Signup check failed:", error);
      
      if (error.response && error.response.status === 409) {
        setShowPasswordField(true);
        setShowOtpField(false);
      } else {
        showSnackbar(error.response?.data?.messages?.[0] || "Signup check failed. Please try again.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    
    if (!otp || otp.length < 4) {
      showSnackbar("Please enter a valid OTP", "error");
      return;
    }

    setOtpLoading(true);

    try {
      const response = await axiosInstance.post(`${FAST_API_BASE_URL}/api/verify-otp`, {
        email: email,
        otp: otp,
      }, {
        headers: {
          "X-API-KEY": process.env.REACT_APP_API_KEY,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 200) {
        showSnackbar("Email verified successfully!", "success");
        setShowSignupForm(true);
        setShowOtpField(false);
      }
    } catch (error) {
      console.error("OTP verification failed:", error);
      showSnackbar(error.response?.data?.messages?.[0] || "OTP verification failed. Please try again.", "error");
    } finally {
      setOtpLoading(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    
    if (!password) {
      showSnackbar("Please enter your password", "error");
      return;
    }

    setPasswordLoading(true);

    try {
      const headers = getHeaders();
      const payload = {
        mail: email,
        password: password,
        type: "custom",
        role: role || "",
        id: Id || "",
      };

      
      let response;
      if (Object.keys(headers).length > 0 || Id) {
        response = await axiosInstance.post(`${FAST_API_BASE_URL}/api/user/login`, payload, {
          headers,
        });
      } else {
        response = await axiosInstance.post(`${FAST_API_BASE_URL}/api/user/re-login`, {
          mail: email,
          password: password,
          type: "custom",
        });
      }

if (response.status === 200 && response.data) {
  const { result } = response.data;
  
  let domains = [];
  let userRole = result.role || "";
  
  if (result.domains) {
    domains = result.domains || [];
    userRole = result.role || (domains[0]?.role) || "";
  } else if (result.domain_id) {
    domains = [{
      domain_id: result.domain_id,
      role: result.role || "",
    }];
    userRole = result.role || "";
  }
  
  if (userRole) {
    localStorage.setItem('role', userRole);
  }
  initializeSession(rememberMe);
  
  const loginSuccess = login(
    {
      id: result.user_id,
      email: email,
      name: result.name,
      profile_pic: result.profile_pic || null,
      is_super_admin: result.is_super_admin === 1 || 
                     result.is_super_admin === true || 
                     result.is_super_admin === "true" ||
                     result.is_super_admin === "1",
      is_whitelabel_user: result.is_whitelabel_user,
      is_whitelabelling_reseller_only: result.is_whitelabelling_reseller_only || false,
    },
    domains
  );

  if (loginSuccess) {
    if (domains.length === 0) {
      handleSuccessfulLoginWithoutDomains(result, email);
    } else {
      handleSuccessfulLogin(result, email, false);
    }
  } else {
    showSnackbar("Login failed. Please try again.", "error");
  }
}
    } catch (error) {
      console.error("Password login error:", error);
      if (error.response && error.response.status === 401) {
        showSnackbar("Invalid email or password. Please try again.", "error");
      } else {
        showSnackbar(error.response?.data?.messages?.[0] || "Invalid credentials. Please try again.", "error");
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSuccessfulLoginWithoutDomains = (responseData, userEmail) => {
    showSnackbar("Login successful! Please register your domain to continue.", "success");
    
    setShowRegistrationForm(true);
    setShowPasswordField(false);
  };

const handleSuccessfulLogin = (responseData, userEmail, isGoogle = false, googleUserData = null) => {
  showSnackbar("Login Successful! Please wait...", "success");
  
  setTimeout(() => {
    const userRole = responseData.role || 
                    (responseData.domains && responseData.domains[0]?.role) || 
                    "";
    
    if (userRole) {
      localStorage.setItem('role', userRole);
    }
    
    const currentRememberMe = localStorage.getItem('rememberMe') === 'true';
    if (!currentRememberMe) {
      localStorage.setItem('showStayLoggedInPrompt', 'true');
    }
    
    const isSuperAdmin = responseData.is_super_admin === 1 || 
                        responseData.is_super_admin === true || 
                        responseData.is_super_admin === "true" || 
                        responseData.is_super_admin === "1";
    
    if (isSuperAdmin) {
      navigate("/customers");
    } else if ((responseData.domains && responseData.domains.length > 0 && responseData.domains[0].role === "read only") || responseData.domain_id) {
      navigate("/dashboard");
    } else {
      const installSource = getInstallSource();
      if (installSource === "hubspot_marketplace") {
        localStorage.removeItem("install_source");
        navigate("/integrations?tab=hubspot");
      } else {
        navigate("/settings");
      }
    }
  }, 1000);
};

  const handleSignupFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!signupFormData.password || !signupFormData.confirmPassword) {
      showSnackbar("Please fill in all password fields", "error");
      return;
    }

    if (signupFormData.password !== signupFormData.confirmPassword) {
      showSnackbar("Passwords do not match!", "error");
      return;
    }

    if (!allRulesPassed) {
      showSnackbar("Password does not meet the required criteria", "error");
      return;
    }

    setSignupLoading(true);

    try {
      const payload = {
        password: signupFormData.password,
        email: email,
        user_name: `${signupFormData.firstName} ${signupFormData.lastName}`.trim(),
        role: "admin",
        referral_code: localStorage.getItem("referral_code") || null
      };

      const headers = getHeaders();
      const response = await axiosInstance.post(
        `${FAST_API_BASE_URL}/api/save-password`,
        payload,
        {
          headers: {
            ...headers,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        showSnackbar("Account created successfully!", "success");
        setShowSignupForm(false);
        setShowRegistrationForm(true);
        localStorage.removeItem("referral_code");
        const loginResponse = await axiosInstance.post(`${FAST_API_BASE_URL}/api/user/re-login`, {
          mail: email,
          password: signupFormData.password,
          type: "custom",
        });

        if (loginResponse.status === 200) {
          const { result } = loginResponse.data;
          const domains = result.domains || [];
          const userRole = result.role || (domains[0]?.role) || "";

          if (userRole) {
            localStorage.setItem('role', userRole);
          }
          initializeSession(false);
          login(
            {
              id: result.user_id,
              email: email,
              name: `${signupFormData.firstName} ${signupFormData.lastName}`.trim(),
              is_super_admin: result.is_super_admin === 1 || 
                             result.is_super_admin === true || 
                             result.is_super_admin === "true" ||
                             result.is_super_admin === "1",
              is_whitelabel_user: result.is_whitelabel_user
            },
            domains
          );
        }
      }
    } catch (err) {
      console.error("Final signup failed:", err);
      showSnackbar("Failed to create account! Please try again.", "error");
    } finally {
      setSignupLoading(false);
    }
  };

  const extractDomainName = (input) => {
    if (!input) return '';
    
    let domain = input.trim().toLowerCase();
    domain = domain.replace(/^https?:\/\//, '');
    domain = domain.split('/')[0];
    domain = domain.split(':')[0];
    
    if (domain.startsWith('www.')) {
      domain = domain.substring(4);
    }
    
    return domain;
  };

  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();

    if (!registrationFormData.domainName || !registrationFormData.websitePlatform || !registrationFormData.companyName) {
      showSnackbar("Please fill in all fields", "error");
      return;
    }

    const cleanDomain = extractDomainName(registrationFormData.domainName);

    if (cleanDomain === "abc.com") {
      showSnackbar("Please contact your admin to add user!", "warning");
      return;
    }

    setDomainRegistrationLoading(true);

    try {
      const payload1 = {
        domain_url: cleanDomain,
        source: registrationFormData.websitePlatform.value,
        email: email,
        first_name: signupFormData.firstName || user?.name?.split(' ')[0] || '',
        last_name: signupFormData.lastName || user?.name?.split(' ')[1] || '',
      };

      const payload = {
        domain: cleanDomain,
        source: registrationFormData.websitePlatform.value,
        company_name: registrationFormData.companyName.trim(),
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
        date_formate: "MM-DD-YYYY",
        email: email,
        user_name: `${signupFormData.firstName || user?.name?.split(' ')[0] || ''} ${signupFormData.lastName || user?.name?.split(' ')[1] || ''}`.trim(),
        is_google_user: false,
        is_user_logged_in: false
      };

      const response1 = await axiosInstance.post(`${LAMBDA_BASE_URL}/save-registration`, payload1, {
        headers: {
          "X-API-KEY": process.env.REACT_APP_API_KEY,
          "Content-Type": "application/json",
        },
      });

      if (registrationFormData.websitePlatform.value === "wordpress" || registrationFormData.websitePlatform.value === "webflow") {
        showSnackbar("Thank you for registering with Ansera", "success");
        
        localStorage.setItem('justRedirected', 'true');
        setTimeout(() => {
          if (registrationFormData.websitePlatform.value === "wordpress") {
            window.location.href = `https://wordpress.org/plugins/ansera-search`;
          } else if (registrationFormData.websitePlatform.value === "webflow") {
            window.location.href = "https://webflow.com/apps/detail/ansera-search";
          }
        }, 3000);
      } 
     
      else if (registrationFormData.websitePlatform.value === "wix" || registrationFormData.websitePlatform.value === "squarespace" || registrationFormData.websitePlatform.value === "custom") {
        const response = await axiosInstance.post(`${FAST_API_BASE_URL}/api/register`, payload, {
          headers: {
            "X-API-KEY": process.env.REACT_APP_API_KEY,
            "Content-Type": "application/json",
          },
        });
        const loginType = user?.profile_pic ? "google" : "custom";
        if (response.status === 200) {
          const loginResponse = await axiosInstance.post(`${FAST_API_BASE_URL}/api/user/re-login`, {
            mail: email,
            password: password,
            type: loginType,
          });

          if (loginResponse.status === 200) {
            const { result } = loginResponse.data;
            login(
              {
                id: result.user_id,
                email: email,
                name: `${signupFormData.firstName} ${signupFormData.lastName}`.trim() || user?.name,
                is_super_admin: result.is_super_admin === 1 || 
                               result.is_super_admin === true || 
                               result.is_super_admin === "true" ||
                               result.is_super_admin === "1",
                is_whitelabel_user: result.is_whitelabel_user
              },
              result.domains || []
            );

            showSnackbar("Registration successful! Redirecting...", "success");
            const installSource = getInstallSource();
            setTimeout(() => {
              if (installSource === "hubspot_marketplace") {
                localStorage.removeItem("install_source");
                navigate('/integrations?tab=hubspot');
              } else {
                navigate('/settings');
              }
            }, 2000);
          }
        }
      }
    } catch (err) {
      console.error("Domain registration failed:", err);
      showSnackbar("Domain registration failed. Please try again.", "error");
    } finally {
      setDomainRegistrationLoading(false);
    }
  };

const handleGoogleSignIn = async (credentialResponse) => {
  setGoogleSignInLoading(true);
  try {
    const decoded = jwtDecode(credentialResponse.credential);
    const headers = getHeaders();

    const payload = {
      mail: decoded.email,
      name: decoded.name,
      profile_pic: decoded.picture,
      role: role || "",
      type: "google",
      id: Id || "",
    };
    let response;
    if (Object.keys(headers).length > 0 || Id) {
      response = await axiosInstance.post(`${FAST_API_BASE_URL}/api/user/login`, payload, {
        headers,
      });
    } else {
      try {
        response = await axiosInstance.post(`${FAST_API_BASE_URL}/api/user/re-login`, {
          mail: decoded.email,
          name: decoded.name,
          profile_pic: decoded.picture,
          type: "google",
        });
      } catch (reLoginError) {
        if (reLoginError.response && reLoginError.response.status === 401) {
          const saveUserPayload = {
            email: decoded.email,
            name: decoded.name,
            profile_pic: decoded.picture,
            referral_code: localStorage.getItem("referral_code") || null
          };

          const saveUserResponse = await axiosInstance.post(
            `${FAST_API_BASE_URL}/api/save-google-user`,
            saveUserPayload,
            {
              headers: {
                "X-API-KEY": process.env.REACT_APP_API_KEY,
                "Content-Type": "application/json",
              },
            }
          );

          if (saveUserResponse.status === 200) {
            showSnackbar("Account created successfully!", "success");
            const loginResponse = await axiosInstance.post(`${FAST_API_BASE_URL}/api/user/re-login`, {
              mail: decoded.email,
              name: decoded.name,
              profile_pic: decoded.picture,
              type: "google",
            });

            if (loginResponse.status === 200) {
              const { result } = loginResponse.data;
              const domains = result.domains || [];
              const userRole = result.role || (domains[0]?.role) || "";
              
              if (userRole) {
                localStorage.setItem('role', userRole);
              }
              initializeSession(false);
              login(
                {
                  id: result.user_id,
                  email: decoded.email,
                  name: result.name,
                  profile_pic: decoded.picture, 
                  is_super_admin: result.is_super_admin === 1 || 
                                 result.is_super_admin === true || 
                                 result.is_super_admin === "true" ||
                                 result.is_super_admin === "1",
                  is_whitelabel_user: result.is_whitelabel_user,
                  is_whitelabelling_reseller_only: result.is_whitelabelling_reseller_only || false,
                },
                domains
              );
              
              if (domains.length === 0) {
                showSnackbar("Please register your domain to continue.", "success");
                setShowRegistrationForm(true);
                setEmail(decoded.email);
                const nameParts = decoded.name.split(' ');
                setSignupFormData(prev => ({
                  ...prev,
                  firstName: nameParts[0] || '',
                  lastName: nameParts.slice(1).join(' ') || ''
                }));
              } else {
                handleSuccessfulLogin(result, decoded.email, true, decoded);
              }
              
              localStorage.removeItem("referral_code");
            }
          }
          return;
        } else {
          throw reLoginError;
        }
      }
    }

    if (response && response.status === 200 && response.data) {
      const { result } = response.data;
      
      let domains = [];
      let userRole = result.role || "";
      
      if (result.domains) {
        domains = result.domains || [];
        userRole = result.role || (domains[0]?.role) || "";
      } else if (result.domain_id) {
        domains = [{
          domain_id: result.domain_id,
          role: result.role || "",
        }];
        userRole = result.role || "";
      }
      
      if (userRole) {
        localStorage.setItem('role', userRole);
      }
      initializeSession(false);
      const loginSuccess = login(
        {
          id: result.user_id,
          email: decoded.email,
          name: result.name,
          profile_pic: result.profile_pic || decoded.picture,
          is_super_admin: result.is_super_admin === 1 || 
                         result.is_super_admin === true || 
                         result.is_super_admin === "true" ||
                         result.is_super_admin === "1",
          is_whitelabel_user: result.is_whitelabel_user,
          is_whitelabelling_reseller_only: result.is_whitelabelling_reseller_only || false,
        },
        domains
      );

      if (loginSuccess) {
        if (domains.length === 0) {
          handleGoogleLoginWithoutDomains(result, decoded.email, decoded);
        } else {
          handleSuccessfulLogin(result, decoded.email, true, decoded);
        }
      }
    } else {
      showSnackbar("Login failed!", "error");
    }
  } catch (error) {
    console.error("Google login error:", error);
    showSnackbar("Failed to login! Please try again.", "error");
  } finally {
    setGoogleSignInLoading(false);
  }
};

const handleGoogleLoginWithoutDomains = (responseData, userEmail, googleUserData) => {
  showSnackbar("Login successful! Please register your domain to continue.", "success");
  
  setShowRegistrationForm(true);
  setEmail(userEmail);
  
  const nameParts = googleUserData.name.split(' ');
  setSignupFormData(prev => ({
    ...prev,
    firstName: nameParts[0] || '',
    lastName: nameParts.slice(1).join(' ') || ''
  }));
};
  const getHeaders = () => {
    const domainId = localStorage.getItem("domain_id");
    const secret = localStorage.getItem("secret");

    if (secret) {
      return { "SECRET-TOKEN": secret };
    }
    if (domainId) {
      return { "DOMAIN-TOKEN": domainId };
    }
    return {};
  };

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    googleLogout();
    logout();
    resetAuthFlow();
    navigate("/login");
  };

  const renderRule = (label, passed) => ( 
    <Box display="flex" alignItems="center" mb={1}> 
      {passed ? ( 
        <CheckCircleIcon sx={{ mr: 1, color: "#219297", fontSize: "16px" }} />
      ) : ( 
        <CancelIcon color="error" sx={{ mr: 1, fontSize: "16px" }} /> 
      )} 
      <Typography variant="body2" color={passed ? "#219297" : (isDarkMode ? "#a0aec0" : "text.secondary")} sx={{ fontSize: "13px" }}> 
        {label} 
      </Typography> 
    </Box>
  );

  const renderProfileCard = () => {
    const shouldShowProfile = isLoggedIn && (showRegistrationForm || showSignupForm);
    
    if (!shouldShowProfile) return null;

    return (
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="body2" sx={{ color: isDarkMode ? "#a0aec0" : "#666", mr: 1 }}>
          Welcome, {userName}
        </Typography>
        <IconButton
          onClick={handleMenuOpen}
          size="small"
        >
          <Avatar
            src={user?.profile_pic || undefined}  
            sx={{ 
              width: 36, 
              height: 36, 
              bgcolor: user?.profile_pic ? 'transparent' : "#219297",
              color: user?.profile_pic ? undefined : "white", 
              fontSize: "14px",
              fontWeight: "bold"
            }}
          >
            {!user?.profile_pic && userName.charAt(0).toUpperCase()}
          </Avatar>
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            elevation: 4,
            sx: {
              mt: 1.5,
              borderRadius: 2,
              overflow: "visible",
              filter: "drop-shadow(0px 4px 12px rgba(0,0,0,0.15))",
              minWidth: 260,
              p: 1,
              backgroundColor: isDarkMode ? "#060C18" : "#fff",
              "& .MuiAvatar-root": {
                width: 48,
                height: 48,
                ml: 1,
                mr: 1.5,
              },
              "&:before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 20,
                width: 10,
                height: 10,
                bgcolor: isDarkMode ? "#060C18" : "#fff",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{
            horizontal: "right",
            vertical: "top",
          }}
          anchorOrigin={{
            horizontal: "right",
            vertical: "bottom",
          }}
        >
          <MenuItem disableRipple sx={{ py: 1.5 }}>
            <Avatar
              src={user?.profile_pic || undefined}
              sx={{
                bgcolor: "#219297",
                color: "white",
                fontSize: "16px",
                fontWeight: "bold"
              }}
            >
              {userName.charAt(0).toUpperCase()}
            </Avatar>
            <div>
              <div style={{ fontWeight: 600, fontSize: "14px", color: isDarkMode ? "#e2e8f0" : "inherit" }}>
                {userName}
              </div>
              <div style={{ fontSize: "0.8rem", color: isDarkMode ? "#a0aec0" : "#555" }}>
                {userEmail}
              </div>
            </div>
          </MenuItem>
          <Divider sx={{ my: 1, borderColor: isDarkMode ? "#2d2d3f" : undefined }} />
          <MenuItem
            onClick={handleLogout}
            sx={{ fontWeight: 500, alignContent: "center", fontSize: "14px", color: isDarkMode ? "#e2e8f0" : undefined }}
          >
            Sign out
          </MenuItem>
        </Menu>
      </Box>
    );
  };

  const renderEmailStep = () => (
    <form onSubmit={handleEmailSubmit}>
      <Box>
        <Box mb={3}>
          <label 
            htmlFor="auth-email" 
            style={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              marginBottom: "8px", 
              display: "block",
              color: isDarkMode ? "#e2e8f0" : "#374151"
            }}
          >
            Email Address
          </label>
          <input
            id="auth-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="Your Email Address"
            style={{
              width: "100%",
              padding: "14px 16px",
              fontSize: "15px",
              border: `2px solid ${isDarkMode ? "#4a5568" : "#e5e7eb"}`,
              borderRadius: "10px",
              boxSizing: "border-box",
              outline: "none",
              transition: "all 0.2s ease",
              fontFamily: "inherit",
              backgroundColor: isDarkMode ? "#2d2d3f" : "#fafafa",
              color: isDarkMode ? "#e2e8f0" : "inherit",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#219297";
              e.target.style.backgroundColor = isDarkMode ? "#3d3d4f" : "#ffffff";
              e.target.style.boxShadow = "0 0 0 3px rgba(33, 146, 151, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = isDarkMode ? "#4a5568" : "#e5e7eb";
              e.target.style.backgroundColor = isDarkMode ? "#2d2d3f" : "#fafafa";
              e.target.style.boxShadow = "none";
            }}
          />
        </Box>
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={loading}
          sx={{
            py: 1.75,
            backgroundColor: "#219297",
            color: "#ffffff",
            borderRadius: "10px",
            fontWeight: 600,
            fontSize: "15px",
            textTransform: "none",
            boxShadow: "0 4px 12px rgba(33, 146, 151, 0.3)",
            "&:hover": {
              backgroundColor: "#1a7a7d",
              boxShadow: "0 6px 16px rgba(33, 146, 151, 0.4)",
              transform: "translateY(-1px)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
            "&:disabled": {
              backgroundColor: isDarkMode ? "#2d2d3f" : "#cbd5e1",
              boxShadow: "none",
              color: isDarkMode ? "#a0aec0" : undefined,
            },
            transition: "all 0.2s ease",
          }}
        >
          {loading ? (
            <Box display="flex" alignItems="center" justifyContent="center">
              <CircularProgress size={20} sx={{ color: "white", mr: 1.5 }} />
              Verifying...
            </Box>
          ) : (
            "Continue with Email"
          )}
        </Button>
      </Box>
    </form>
  );

  const renderOtpStep = () => (
    <form onSubmit={handleOtpSubmit}>
      <Box>
        <Box 
          sx={{ 
            mb: 3,
            p: 2,
            backgroundColor: isDarkMode ? "#0a1020" : "#f0fdfa",
            borderRadius: "10px",
            border: `1px solid ${isDarkMode ? "#2d2d3f" : "#ccfbf1"}`,
            textAlign: "center"
          }}
        >
          <Typography variant="body2" sx={{ color: isDarkMode ? "#a0aec0" : "text.secondary", fontSize: "14px" }}>
            Code sent to
          </Typography>
          <Typography variant="body1" fontWeight="600" sx={{ color: "#219297", mt: 0.5 }}>
            {email}
          </Typography>
        </Box>
        
        <Box mb={3}>
          <label 
            htmlFor="otp-input" 
            style={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              marginBottom: "8px", 
              display: "block",
              color: isDarkMode ? "#e2e8f0" : "#374151"
            }}
          >
            Verification Code
          </label>
          <input
            id="otp-input"
            type="text"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
            required
            placeholder="Enter 6-digit code"
            maxLength={6}
            style={{
              width: "100%",
              padding: "14px 16px",
              fontSize: "18px",
              border: `2px solid ${isDarkMode ? "#4a5568" : "#e5e7eb"}`,
              borderRadius: "10px",
              boxSizing: "border-box",
              outline: "none",
              transition: "all 0.2s ease",
              fontFamily: "monospace",
              letterSpacing: "0.3em",
              textAlign: "center",
              fontWeight: "600",
              backgroundColor: isDarkMode ? "#2d2d3f" : "#fafafa",
              color: isDarkMode ? "#e2e8f0" : "inherit",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#219297";
              e.target.style.backgroundColor = isDarkMode ? "#3d3d4f" : "#ffffff";
              e.target.style.boxShadow = "0 0 0 3px rgba(33, 146, 151, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = isDarkMode ? "#4a5568" : "#e5e7eb";
              e.target.style.backgroundColor = isDarkMode ? "#2d2d3f" : "#fafafa";
              e.target.style.boxShadow = "none";
            }}
          />
        </Box>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={otpLoading}
          sx={{
            py: 1.75,
            backgroundColor: "#219297",
            color: "#ffffff",
            borderRadius: "10px",
            fontWeight: 600,
            fontSize: "15px",
            textTransform: "none",
            boxShadow: "0 4px 12px rgba(33, 146, 151, 0.3)",
            "&:hover": {
              backgroundColor: "#1a7a7d",
              boxShadow: "0 6px 16px rgba(33, 146, 151, 0.4)",
              transform: "translateY(-1px)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
            "&:disabled": {
              backgroundColor: isDarkMode ? "#2d2d3f" : "#cbd5e1",
              boxShadow: "none",
              color: isDarkMode ? "#a0aec0" : undefined,
            },
            transition: "all 0.2s ease",
          }}
        >
          {otpLoading ? (
            <Box display="flex" alignItems="center" justifyContent="center">
              <CircularProgress size={20} sx={{ color: "white", mr: 1.5 }} />
              Verifying code...
            </Box>
          ) : (
            "Verify & Continue"
          )}
        </Button>

        <Button
          variant="text"
          fullWidth
          onClick={resetAuthFlow}
          sx={{
            mt: 2,
            color: "#219297",
            textTransform: "none",
            fontWeight: 500,
            fontSize: "14px",
            "&:hover": {
              backgroundColor: isDarkMode ? "#2d2d3f" : "#f0fdfa",
            },
          }}
        >
          ← Change Email Address
        </Button>
      </Box>
    </form>
  );

  const renderPasswordStep = () => {
    if (forgotMode) {
      return renderForgotPasswordStep();
    }

    return (
      <form onSubmit={handlePasswordLogin}>
        <Box>
          <Box 
            sx={{ 
              mb: 3,
              p: 2,
              backgroundColor: isDarkMode ? "#0a1020" : "#f0fdfa",
              borderRadius: "10px",
              border: `1px solid ${isDarkMode ? "#2d2d3f" : "#ccfbf1"}`,
            }}
          >
            <Typography variant="body2" sx={{ color: isDarkMode ? "#a0aec0" : "text.secondary", fontSize: "14px", mb: 1 }}>
              Welcome back! Logging in as
            </Typography>
            <Typography variant="body1" fontWeight="600" sx={{ color: "#219297" }}>
              {email}
            </Typography>
          </Box>
          
          <Box mb={2} sx={{ position: "relative" }}>
            <label 
              htmlFor="login-password" 
              style={{ 
                fontSize: "14px", 
                fontWeight: "600", 
                marginBottom: "8px", 
                display: "block",
                color: isDarkMode ? "#e2e8f0" : "#374151"
              }}
            >
              Password
            </label>
            <input
              id="login-password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password"
              style={{
                width: "100%",
                padding: "12px 40px 12px 14px",
                fontSize: "14px",
                border: `2px solid ${isDarkMode ? "#4a5568" : "#e5e7eb"}`,
                borderRadius: "10px",
                boxSizing: "border-box",
                outline: "none",
                transition: "all 0.2s ease",
                backgroundColor: isDarkMode ? "#2d2d3f" : "#fafafa",
                color: isDarkMode ? "#e2e8f0" : "inherit",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#219297";
                e.target.style.backgroundColor = isDarkMode ? "#3d3d4f" : "#ffffff";
                e.target.style.boxShadow = "0 0 0 3px rgba(33, 146, 151, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = isDarkMode ? "#4a5568" : "#e5e7eb";
                e.target.style.backgroundColor = isDarkMode ? "#2d2d3f" : "#fafafa";
                e.target.style.boxShadow = "none";
              }}
            />
            <IconButton
              onClick={() => setShowPassword(!showPassword)}
              sx={{
                position: "absolute",
                right: "8px",
                top: "34px",
                padding: "8px",
              }}
            >
              {showPassword ? <VisibilityOff fontSize="small" sx={{ color: isDarkMode ? "#a0aec0" : undefined }} /> : <Visibility fontSize="small" sx={{ color: isDarkMode ? "#a0aec0" : undefined }} />}
            </IconButton>
          </Box>

          <Box width="100%" display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Box display="flex" alignItems="center">
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{
                  width: "16px",
                  height: "16px",
                  marginRight: "8px",
                  cursor: "pointer",
                  accentColor: "#219297",
                }}
              />
              <label 
                htmlFor="rememberMe" 
                style={{ 
                  fontSize: "13px", 
                  color: isDarkMode ? "#e2e8f0" : "#374151",
                  cursor: "pointer",
                  userSelect: "none"
                }}
              >
                Keep me signed in
              </label>
            </Box>
              
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={enterForgotMode}
              sx={{ 
                color: "#219297", 
                fontWeight: 500,
                fontSize: "14px",
                textDecoration: "none",
                "&:hover": {
                  textDecoration: "underline",
                }
              }}
            >
              Forgot password?
            </Link>
          </Box>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={passwordLoading}
            sx={{
              py: 1.75,
              backgroundColor: "#219297",
              color: "#ffffff",
              borderRadius: "10px",
              fontWeight: 600,
              fontSize: "15px",
              textTransform: "none",
              boxShadow: "0 4px 12px rgba(33, 146, 151, 0.3)",
              "&:hover": {
                backgroundColor: "#1a7a7d",
                boxShadow: "0 6px 16px rgba(33, 146, 151, 0.4)",
                transform: "translateY(-1px)",
              },
              "&:active": {
                transform: "translateY(0)",
              },
              "&:disabled": {
                backgroundColor: isDarkMode ? "#2d2d3f" : "#cbd5e1",
                boxShadow: "none",
                color: isDarkMode ? "#a0aec0" : undefined,
              },
              transition: "all 0.2s ease",
            }}
          >
            {passwordLoading ? (
              <Box display="flex" alignItems="center" justifyContent="center">
                <CircularProgress size={20} sx={{ color: "white", mr: 1.5 }} />
                Logging in...
              </Box>
            ) : (
              "Login"
            )}
          </Button>

          <Button
            variant="text"
            fullWidth
            onClick={resetAuthFlow}
            sx={{
              mt: 2,
              color: "#219297",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "14px",
              "&:hover": {
                backgroundColor: isDarkMode ? "#2d2d3f" : "#f0fdfa",
              },
            }}
          >
            ← Change Email Address
          </Button>
        </Box>
      </form>
    );
  };

  const renderForgotPasswordStep = () => (
    <form onSubmit={handleSendReset}>
      <Box>
        <Box mb={3}>
          <label 
            htmlFor="forgot-email" 
            style={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              marginBottom: "8px", 
              display: "block",
              color: isDarkMode ? "#e2e8f0" : "#374151"
            }}
          >
            Email Address
          </label>
          <input
            id="forgot-email"
            type="email"
            value={forgotEmail}
            onChange={handleForgotEmailChange}
            required
            placeholder="Enter your email address"
            style={{
              width: "100%",
              padding: "14px 16px",
              fontSize: "15px",
              border: `2px solid ${isDarkMode ? "#4a5568" : "#e5e7eb"}`,
              borderRadius: "10px",
              boxSizing: "border-box",
              outline: "none",
              transition: "all 0.2s ease",
              fontFamily: "inherit",
              backgroundColor: isDarkMode ? "#2d2d3f" : "#fafafa",
              color: isDarkMode ? "#e2e8f0" : "inherit",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#219297";
              e.target.style.backgroundColor = isDarkMode ? "#3d3d4f" : "#ffffff";
              e.target.style.boxShadow = "0 0 0 3px rgba(33, 146, 151, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = isDarkMode ? "#4a5568" : "#e5e7eb";
              e.target.style.backgroundColor = isDarkMode ? "#2d2d3f" : "#fafafa";
              e.target.style.boxShadow = "none";
            }}
          />
        </Box>

        {forgotMessage && (
          <Typography
            variant="body2"
            align="center"
            sx={{
              mb: 2,
              color: forgotSuccess ? "success.main" : "error.main",
              fontSize: "0.95rem",
            }}
          >
            {forgotMessage}
          </Typography>
        )}

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={forgotLoading || forgotSuccess}
          sx={{
            py: 1.75,
            backgroundColor: forgotSuccess ? (isDarkMode ? "#2d2d3f" : "#ccc") : "#219297",
            color: "#ffffff",
            borderRadius: "10px",
            fontWeight: 600,
            fontSize: "15px",
            textTransform: "none",
            boxShadow: forgotSuccess ? "none" : "0 4px 12px rgba(33, 146, 151, 0.3)",
            "&:hover": {
              backgroundColor: forgotSuccess ? (isDarkMode ? "#2d2d3f" : "#ccc") : "#1a7a7d",
              boxShadow: forgotSuccess ? "none" : "0 6px 16px rgba(33, 146, 151, 0.4)",
            },
            "&:disabled": {
              backgroundColor: isDarkMode ? "#2d2d3f" : "#cbd5e1",
              boxShadow: "none",
              color: isDarkMode ? "#a0aec0" : undefined,
            },
          }}
        >
          {forgotLoading ? (
            <Box display="flex" alignItems="center" justifyContent="center">
              <CircularProgress size={20} sx={{ color: "white", mr: 1.5 }} />
              Sending...
            </Box>
          ) : forgotSuccess ? (
            "Reset Link Sent"
          ) : (
            "Send Reset Link"
          )}
        </Button>

        <Button
          variant="text"
          fullWidth
          onClick={exitForgotMode}
          sx={{
            mt: 2,
            color: "#219297",
            textTransform: "none",
            fontWeight: 500,
            fontSize: "14px",
            "&:hover": {
              backgroundColor: isDarkMode ? "#2d2d3f" : "#f0fdfa",
            },
          }}
        >
          ← Back to Login
        </Button>
      </Box>
    </form>
  );

  const renderSignupForm = () => (
    <form onSubmit={handleSignupFormSubmit}>
      <Box>
        <Box 
          sx={{ 
            mb: 3,
            p: 2,
            backgroundColor: isDarkMode ? "#0a1020" : "#f0fdfa",
            borderRadius: "10px",
            border: `1px solid ${isDarkMode ? "#2d2d3f" : "#ccfbf1"}`,
          }}
        >
          <Typography variant="body2" sx={{ color: isDarkMode ? "#a0aec0" : "text.secondary", fontSize: "14px", mb: 1 }}>
            Completing signup for
          </Typography>
          <Typography variant="body1" fontWeight="600" sx={{ color: "#219297" }}>
            {email}
          </Typography>
        </Box>
        
        <Grid container spacing={2} mb={3}>
          <Grid item xs={6}>
            <Box mb={2}>
              <label 
                htmlFor="firstName" 
                style={{ 
                  fontSize: "14px", 
                  fontWeight: "600", 
                  marginBottom: "8px", 
                  display: "block",
                  color: isDarkMode ? "#e2e8f0" : "#374151"
                }}
              >
                First Name
              </label>
              <input
                id="firstName"
                type="text"
                value={signupFormData.firstName}
                onChange={(e) => setSignupFormData(prev => ({...prev, firstName: e.target.value}))}
                required
                placeholder="First Name"
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  fontSize: "14px",
                  border: `1px solid ${isDarkMode ? "#4a5568" : "#ccc"}`,
                  borderRadius: "8px",
                  boxSizing: "border-box",
                  backgroundColor: isDarkMode ? "#2d2d3f" : "#fff",
                  color: isDarkMode ? "#e2e8f0" : "inherit",
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box mb={2}>
              <label 
                htmlFor="lastName" 
                style={{ 
                  fontSize: "14px", 
                  fontWeight: "600", 
                  marginBottom: "8px", 
                  display: "block",
                  color: isDarkMode ? "#e2e8f0" : "#374151"
                }}
              >
                Last Name
              </label>
              <input
                id="lastName"
                type="text"
                value={signupFormData.lastName}
                onChange={(e) => setSignupFormData(prev => ({...prev, lastName: e.target.value}))}
                required
                placeholder="Last Name"
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  fontSize: "14px",
                  border: `1px solid ${isDarkMode ? "#4a5568" : "#ccc"}`,
                  borderRadius: "8px",
                  boxSizing: "border-box",
                  backgroundColor: isDarkMode ? "#2d2d3f" : "#fff",
                  color: isDarkMode ? "#e2e8f0" : "inherit",
                }}
              />
            </Box>
          </Grid>
        </Grid>

        <Box mb={2} sx={{ position: "relative" }}>
          <label 
            htmlFor="password" 
            style={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              marginBottom: "8px", 
              display: "block",
              color: isDarkMode ? "#e2e8f0" : "#374151"
            }}
          >
            Password
          </label>
          <input
            id="password"
            type={showSignupPassword ? "text" : "password"}
            value={signupFormData.password}
            onChange={(e) => setSignupFormData(prev => ({...prev, password: e.target.value}),
                                setPassword(e.target.value))}
            required
            placeholder="Enter password"
            style={{
              width: "100%",
              padding: "12px 40px 12px 14px",
              fontSize: "14px",
              border: `1px solid ${isDarkMode ? "#4a5568" : "#ccc"}`,
              borderRadius: "8px",
              boxSizing: "border-box",
              backgroundColor: isDarkMode ? "#2d2d3f" : "#fff",
              color: isDarkMode ? "#e2e8f0" : "inherit",
            }}
          />
          <IconButton
            onClick={() => setShowSignupPassword(!showSignupPassword)}
            sx={{
              position: "absolute",
              right: "8px",
              top: "34px",
              padding: "8px",
            }}
          >
            {showSignupPassword ? <VisibilityOff fontSize="small" sx={{ color: isDarkMode ? "#a0aec0" : undefined }} /> : <Visibility fontSize="small" sx={{ color: isDarkMode ? "#a0aec0" : undefined }} />}
          </IconButton>
        </Box>

        {signupFormData.password && (
          <Box mb={2} sx={{ mt: 1 }}>
            {renderRule("At least 8 characters", passwordRules.minLength)}
            {renderRule("Includes a number", passwordRules.number)}
            {renderRule("Includes a special character", passwordRules.specialChar)}
            {renderRule("Includes Uppercase [A-Z]", passwordRules.uppercase)}
            {renderRule("Includes Lowercase [a-z]", passwordRules.lowercase)}
          </Box>
        )}

        <Box mb={3} sx={{ position: "relative" }}>
          <label 
            htmlFor="confirmPassword" 
            style={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              marginBottom: "8px", 
              display: "block",
              color: isDarkMode ? "#e2e8f0" : "#374151"
            }}
          >
            Confirm Password
          </label>
          <input
            id="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            value={signupFormData.confirmPassword}
            onChange={(e) => setSignupFormData(prev => ({...prev, confirmPassword: e.target.value}))}
            required
            placeholder="Confirm password"
            style={{
              width: "100%",
              padding: "12px 40px 12px 14px",
              fontSize: "14px",
              border: `1px solid ${isDarkMode ? "#4a5568" : "#ccc"}`,
              borderRadius: "8px",
              boxSizing: "border-box",
              backgroundColor: isDarkMode ? "#2d2d3f" : "#fff",
              color: isDarkMode ? "#e2e8f0" : "inherit",
            }}
          />
          <IconButton
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            sx={{
              position: "absolute",
              right: "8px",
              top: "34px",
              padding: "8px",
            }}
          >
            {showConfirmPassword ? <VisibilityOff fontSize="small" sx={{ color: isDarkMode ? "#a0aec0" : undefined }} /> : <Visibility fontSize="small" sx={{ color: isDarkMode ? "#a0aec0" : undefined }} />}
          </IconButton>
        </Box>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={signupLoading || !allRulesPassed}
          sx={{
            py: 1.75,
            backgroundColor: (!allRulesPassed) ? (isDarkMode ? "#2d2d3f" : "#ccc") : "#219297",
            color: "#ffffff",
            borderRadius: "10px",
            fontWeight: 600,
            fontSize: "15px",
            textTransform: "none",
            boxShadow: allRulesPassed ? "0 4px 12px rgba(33, 146, 151, 0.3)" : "none",
            "&:hover": {
              backgroundColor: allRulesPassed ? "#1a7a7d" : (isDarkMode ? "#2d2d3f" : "#ccc"),
              boxShadow: allRulesPassed ? "0 6px 16px rgba(33, 146, 151, 0.4)" : "none",
            },
            "&:disabled": {
              backgroundColor: isDarkMode ? "#2d2d3f" : "#cbd5e1",
              boxShadow: "none",
              color: isDarkMode ? "#a0aec0" : undefined,
            },
          }}
        >
          {signupLoading ? (
            <Box display="flex" alignItems="center" justifyContent="center">
              <CircularProgress size={20} sx={{ color: "white", mr: 1.5 }} />
              Creating Account...
            </Box>
          ) : (
            "Complete Signup"
          )}
        </Button>
      </Box>
    </form>
  );

  const renderRegistrationForm = () => (
    <form onSubmit={handleRegistrationSubmit}>
      <Box>
        <Box mb={3}>
          <label 
            htmlFor="domainName" 
            style={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              marginBottom: "8px", 
              display: "block",
              color: isDarkMode ? "#e2e8f0" : "#374151"
            }}
          >
            Website Domain Name
          </label>
          <input
            id="domainName"
            name="domainName"
            placeholder="example.com"
            value={registrationFormData.domainName}
            onChange={(e) => setRegistrationFormData(prev => ({...prev, domainName: e.target.value}))}
            required
            style={{
              width: "100%",
              padding: "12px 14px",
              fontSize: "14px",
              border: `1px solid ${isDarkMode ? "#4a5568" : "#ccc"}`,
              borderRadius: "8px",
              boxSizing: "border-box",
              backgroundColor: isDarkMode ? "#2d2d3f" : "#fff",
              color: isDarkMode ? "#e2e8f0" : "inherit",
            }}
          />
        </Box>

        <Box mb={3}>
          <label 
            htmlFor="websitePlatform" 
            style={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              marginBottom: "8px", 
              display: "block",
              color: isDarkMode ? "#e2e8f0" : "#374151"
            }}
          >
            Website Platform
          </label>
          <SoftSelect
            value={registrationFormData.websitePlatform}
            options={websitePlatforms}
            onChange={(value) => setRegistrationFormData(prev => ({...prev, websitePlatform: value}))}
            placeholder="Select Website Platform"
            required
            sx={{
              width: "100%",
              "& .MuiSelect-select": {
                padding: "12px 14px",
                fontSize: "14px",
                color: isDarkMode ? "#e2e8f0" : undefined,
              },
              "& .MuiOutlinedInput-root": {
                backgroundColor: isDarkMode ? "#2d2d3f" : "#fff",
                '& fieldset': {
                  borderColor: isDarkMode ? "#4a5568" : "#ccc",
                },
              },
            }}
          />
        </Box>

        <Box mb={3}>
          <label 
            htmlFor="companyName" 
            style={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              marginBottom: "8px", 
              display: "block",
              color: isDarkMode ? "#e2e8f0" : "#374151"
            }}
          >
            Company Name
          </label>
          <input
            id="companyName"
            name="companyName"
            placeholder="Sample Inc."
            value={registrationFormData.companyName}
            onChange={(e) => setRegistrationFormData(prev => ({...prev, companyName: e.target.value}))}
            required
            style={{
              width: "100%",
              padding: "12px 14px",
              fontSize: "14px",
              border: `1px solid ${isDarkMode ? "#4a5568" : "#ccc"}`,
              borderRadius: "8px",
              boxSizing: "border-box",
              backgroundColor: isDarkMode ? "#2d2d3f" : "#fff",
              color: isDarkMode ? "#e2e8f0" : "inherit",
            }}
          />
        </Box>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={domainRegistrationLoading}
          sx={{
            py: 1.75,
            backgroundColor: "#219297",
            color: "#ffffff",
            borderRadius: "10px",
            fontWeight: 600,
            fontSize: "15px",
            textTransform: "none",
            boxShadow: "0 4px 12px rgba(33, 146, 151, 0.3)",
            "&:hover": {
              backgroundColor: "#1a7a7d",
              boxShadow: "0 6px 16px rgba(33, 146, 151, 0.4)",
            },
            "&:disabled": {
              backgroundColor: isDarkMode ? "#2d2d3f" : "#cbd5e1",
              boxShadow: "none",
              color: isDarkMode ? "#a0aec0" : undefined,
            },
          }}
        >
          {domainRegistrationLoading ? (
            <Box display="flex" alignItems="center" justifyContent="center">
              <CircularProgress size={20} sx={{ color: "white", mr: 1.5 }} />
              Registering Domain...
            </Box>
          ) : (
            "Complete Registration"
          )}
        </Button>
      </Box>
    </form>
  );

  const getCurrentStepContent = () => {
    if (showRegistrationForm) return renderRegistrationForm();
    if (showSignupForm) return renderSignupForm();
    if (showPasswordField) return renderPasswordStep();
    if (showOtpField) return renderOtpStep();
    return renderEmailStep();
  };

  const getStepTitle = () => {
    if (showRegistrationForm) return "Domain Registration";
    if (showSignupForm) return "Complete Your Signup";
    if (forgotMode) return "Forgot Password";
    if (showPasswordField) return "Welcome Back";
    if (showOtpField) return "Verify Your Email";
    return "Login/Signup to Ansera AI";
  };

  const getStepSubtitle = () => {
    if (showRegistrationForm) return "Add your domain details to complete registration";
    if (showSignupForm) return "Set up your password to complete registration";
    if (forgotMode) return "Enter your email and we'll send a reset link";
    if (showPasswordField) return "Enter your password to continue";
    if (showOtpField) return "Enter the verification code we sent to your email";
    return "To access your dashboard";
  };

  return (
    <>
      <AppBar
        position="static"
        color="inherit"
        sx={{
          boxShadow: "none",
          backgroundColor: isDarkMode ? "#060C18" : "white",
          borderBottom: `1px solid ${isDarkMode ? "#2d2d3f" : "#e0e0e0"}`,
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", px: 2 }}>
          <Box display="flex" alignItems="center">
            <img 
              src={logo} 
              alt="Ansera Logo" 
              style={{ 
                height: "3rem",
                filter: isDarkMode ? "brightness(0) invert(1)" : "none",
              }} 
            />
          </Box>
          
          {renderProfileCard()}
        </Toolbar>
      </AppBar>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={(props) => <Slide {...props} direction="down" />}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: "100%",
            color: "#fff",
            backgroundColor:
              snackbarSeverity === "success"
                ? "#219297"
                : snackbarSeverity === "error"
                ? "#d32f2f"
                : snackbarSeverity === "warning"
                ? "#ed6c02"
                : undefined,
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <SoftBox
        sx={{
          minHeight: "calc(100dvh - 64px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgColor: isDarkMode ? "#060C18" : "#f0f4f7",
          px: 2,
          py: { xs: 2, sm: 2 },
          overflowY: "auto",
          transition: "background-color 0.3s ease",
        }}
      >
        <Grid container spacing={4} alignItems="center" justifyContent="center" maxWidth="xl">
          {!isMobile && !showRegistrationForm && !showSignupForm && !showPasswordField && !showOtpField && !forgotMode && (
            <Grid item md={6} lg={6} xl={6} sx={{ display: { xs: "none", md: "block" } }}>
              <Box
                component="img"
                src={cartoonImage}
                alt="Login Illustration"
                sx={{
                  width: "100%",
                  maxWidth: { md: "350px", lg: "450px", xl: "550px" },
                  mx: "auto",
                }}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={10} md={6} lg={6} xl={6}>
            <Card
              sx={{
                p: { xs: 3, sm: 4 },
                maxWidth: 480,
                mx: "auto",
                boxShadow: isDarkMode ? "0 10px 30px rgba(0,0,0,0.3)" : "0 10px 30px rgba(0,0,0,0.05)",
                borderRadius: 4,
                backgroundColor: isDarkMode ? "#060C18" : "#fff",
                transition: "background-color 0.3s ease, box-shadow 0.3s ease",
              }}
            >
              <Grid container direction="column" spacing={3} alignItems="center">
                <Grid item>
                  <Typography
                    fontWeight="bold"
                    align="center"
                    sx={{
                      fontSize: "clamp(1.4rem, 2.5vw, 2rem)",
                      color: isDarkMode ? "#e2e8f0" : "text.primary",
                    }}
                  >
                    {getStepTitle()}
                  </Typography>
                  <Typography
                    variant="body2"
                    align="center"
                    sx={{
                      fontSize: "clamp(0.8rem, 1.2vw, 1rem)",
                      color: isDarkMode ? "#a0aec0" : "text.secondary",
                    }}
                  >
                    {getStepSubtitle()}
                  </Typography>
                </Grid>

                <Grid item sx={{ width: "100%" }}>
                  {getCurrentStepContent()}
                </Grid>

                {!showRegistrationForm && !showSignupForm && !showPasswordField && !showOtpField && !forgotMode && (
                  <>
                    <Grid item>
                      <Typography
                        variant="body2"
                        align="center"
                        sx={{ fontSize: "clamp(0.8rem, 1vw, 1rem)", color: isDarkMode ? "#a0aec0" : "text.secondary" }}
                      >
                        OR
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Typography
                        variant="body2"
                        align="center"
                        sx={{
                          mb: 1,
                          fontSize: "clamp(0.8rem, 1vw, 0.95rem)",
                          color: isDarkMode ? "#a0aec0" : "text.secondary",
                        }}
                      >
                        Sign in using Google
                      </Typography>
                      <GoogleLogin
                        theme="filled_blue"
                        text="continue_with"
                        onSuccess={handleGoogleSignIn}
                        onError={() => console.log("Google login failed")}
                      />
                      {googleSignInLoading && (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                          <CircularProgress size={24} sx={{ color: "#219297" }} />
                          <Typography variant="body2" sx={{ ml: 1, color: isDarkMode ? "#e2e8f0" : undefined }}>
                            Signing in with Google...
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  </>
                )}
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </>
  );
};

export default Auth;